/**
 * TablePagination - Reusable pagination controls for tables
 *
 * @version 1.0
 * @updated 2026-02-16
 * @module core/layouts/panels/LeftPanel
 *
 * @description
 * Controles de paginación con glassmorphism para tablas.
 * Incluye navegación First, Previous, Next, Last y indicador de página actual.
 *
 * @remarks
 * **Características**:
 * - Navegación completa: Primera, Anterior, Siguiente, Última página
 * - Indicador de página actual (ej. "Página 2 de 5")
 * - Botones deshabilitados en límites (primera/última página)
 * - Soporte para teclado (Enter, Space)
 * - Accesibilidad completa con ARIA labels
 * - Glassmorphism consistente con diseño WESTEER
 *
 * @example
 * ```tsx
 * <TablePagination
 *   currentPage={2}
 *   totalPages={5}
 *   onPageChange={(page) => setCurrentPage(page)}
 *   mode="compact"
 * />
 * ```
 */

import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import './table-pagination.css';

/**
 * Props para TablePagination
 */
export interface TablePaginationProps {
  /**
   * Página actual (base 1)
   */
  currentPage: number;

  /**
   * Total de páginas
   */
  totalPages: number;

  /**
   * Callback al cambiar de página
   * @param page - Nueva página (base 1)
   */
  onPageChange: (page: number) => void;

  /**
   * Modo de visualización
   * @default "compact"
   */
  mode?: 'compact' | 'expanded';
}

/**
 * TablePagination Component
 */
export function TablePagination({
  currentPage,
  totalPages,
  onPageChange,
  mode = 'compact',
}: TablePaginationProps): JSX.Element {
  // Input controlado para ir a página específica
  const [inputValue, setInputValue] = useState<string>(String(currentPage));

  // Sincroniza el input cuando la página cambia externamente
  useEffect(() => {
    setInputValue(String(currentPage));
  }, [currentPage]);

  /**
   * Confirma la página ingresada manualmente.
   * Valida rango [1, totalPages] antes de navegar.
   */
  const commitInput = (): void => {
    const parsed = parseInt(inputValue, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= totalPages) {
      onPageChange(parsed);
    } else {
      setInputValue(String(currentPage)); // revertir si inválido
    }
  };

  // Estados de navegación
  const canGoFirst = currentPage > 1;
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;
  const canGoLast = currentPage < totalPages;

  /**
   * Navegar a primera página
   */
  const handleFirst = (): void => {
    if (canGoFirst) {
      onPageChange(1);
    }
  };

  /**
   * Navegar a página anterior
   */
  const handlePrevious = (): void => {
    if (canGoPrevious) {
      onPageChange(currentPage - 1);
    }
  };

  /**
   * Navegar a página siguiente
   */
  const handleNext = (): void => {
    if (canGoNext) {
      onPageChange(currentPage + 1);
    }
  };

  /**
   * Navegar a última página
   */
  const handleLast = (): void => {
    if (canGoLast) {
      onPageChange(totalPages);
    }
  };

  // Si no hay páginas, no renderizar nada
  if (totalPages === 0) {
    return <></>;
  }

  return (
    <nav
      className={`table-pagination ${mode}`}
      role="navigation"
      aria-label="Paginación de tabla"
    >
      {/* Primera página */}
      <button
        onClick={handleFirst}
        disabled={!canGoFirst}
        className="pagination-btn first"
        aria-label="Primera página"
        title="Primera página"
      >
        <ChevronsLeft size={16} />
      </button>

      {/* Página anterior */}
      <button
        onClick={handlePrevious}
        disabled={!canGoPrevious}
        className="pagination-btn previous"
        aria-label="Página anterior"
        title="Página anterior"
      >
        <ChevronLeft size={16} />
      </button>

      {/* Input de página (editable, con Enter para navegar) */}
      <input
        type="number"
        className="pagination-input"
        value={inputValue}
        min={1}
        max={totalPages}
        onChange={e => setInputValue(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') commitInput(); }}
        onBlur={commitInput}
        aria-label={`Página actual: ${currentPage} de ${totalPages}`}
        title={`Ir a página (1–${totalPages})`}
      />
      <span className="page-indicator-separator">/ {totalPages}</span>

      {/* Página siguiente */}
      <button
        onClick={handleNext}
        disabled={!canGoNext}
        className="pagination-btn next"
        aria-label="Página siguiente"
        title="Página siguiente"
      >
        <ChevronRight size={16} />
      </button>

      {/* Última página */}
      <button
        onClick={handleLast}
        disabled={!canGoLast}
        className="pagination-btn last"
        aria-label="Última página"
        title="Última página"
      >
        <ChevronsRight size={16} />
      </button>
    </nav>
  );
}
